import { recoverExpenseMoves } from "./lib/expense_move";
import { installFeelTokens } from "./lib/feel";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { AppErrorBoundary } from "./features/common/error_boundary";
import { AuthProvider } from "./hooks/use_auth";
import { prepareNativeBoot } from "./lib/native_boot";
import { setHapticsSink } from "./lib/haptics";
import { startTheme } from "./lib/theme";
import { installNativeBridge } from "@/native/runtime";
import { bridge } from "@/native/impl";
import "./index.css";

// Reconcile the saved palette (the inline no-flash script only set data-theme)
// and follow live OS light/dark changes while in "system" mode.
installNativeBridge(bridge);
startTheme();
installFeelTokens();
setHapticsSink((kind) => bridge.haptics(kind));

const root = document.getElementById("root");
if (!root) throw new Error("No #root element to mount into.");
const mount: HTMLElement = root;

// The boundary sits outside the router and the auth provider on purpose. A lazy
// route chunk that fails to load throws during *render*, and a boundary inside
// the tree it is meant to catch cannot catch it.
//
// On a phone, the session and the outbox are settled first (`native_boot.ts`):
// the auth provider reads the session on mount, and the ledger must hold the
// refilled rows before anything renders from it. On the web this resolves at
// once. Whatever happens, the app renders.
function render(): void {
  createRoot(mount).render(
    <StrictMode>
      <AppErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </StrictMode>,
  );
}

prepareNativeBoot(bridge)
  .catch((cause: unknown) => {
    // Reported, and the app renders anyway (INV-4, INV-7).
    console.warn("Local boot did not finish:", cause instanceof Error ? cause.message : cause);
  })
  .finally(() => { render(); void recoverExpenseMoves().then(count => import("./lib/sync").then(sync => sync.reportMoveRecoveryFailures(count))); });
